//src/dashboard/ChatbotSettings.jsx

import React, { useState, useEffect, useContext } from 'react';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import { AuthContext } from '../context/AuthContext';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import ChatbotPreview from '../components/ChatbotPreview';
import CircularProgress from '@mui/material/CircularProgress';

async function getUserProperties(uid) {
  const propertiesRef = collection(db, 'users', uid, 'properties');
  const snapshot = await getDocs(propertiesRef);
  return snapshot.docs.map((d) => d.data());
}

export default function ChatbotSettings() {
  const { user } = useContext(AuthContext);
  const intl = useIntl();

  const [assistantName, setAssistantName] = useState('');
  const [responseStyle, setResponseStyle] = useState('objective');
  const [initialGreeting, setInitialGreeting] = useState('');
  const [defaultLanguage, setDefaultLanguage] = useState('pt');
  const [primaryColor, setPrimaryColor] = useState('#00aeef');
  const [askNameProactively, setAskNameProactively] = useState(true);

  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [properties, setProperties] = useState([]);

  const [whatsappContacts, setWhatsappContacts] = useState([]);
  const [newContactName, setNewContactName] = useState('');
  const [newContactNumber, setNewContactNumber] = useState('');
  const [newContactType, setNewContactType] = useState('Corretor');
  const [newContactIsDefault, setNewContactIsDefault] = useState(false);

  const [editingContactId, setEditingContactId] = useState(null);
  const [isContactSaving, setIsContactSaving] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;
    async function fetchSettings() {
      const ref = doc(db, 'users', user.uid, 'settings', 'chatbot');
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setAssistantName(data.assistantName || '');
        setResponseStyle(data.responseStyle || 'objective');
        setInitialGreeting(data.initialGreeting || '');
        setDefaultLanguage(data.defaultLanguage || 'pt');
        setPrimaryColor(data.primaryColor || '#00aeef');
        setAskNameProactively(data.askNameProactively ?? true);
        const contacts = (data.whatsapp_contacts || []).map((c) => ({
          ...c,
          status: c.status || 'disponivel',
        }));
        setWhatsappContacts(contacts);
      }
    }
    fetchSettings();
  }, [user?.uid]);

  useEffect(() => {
    if (editingContactId) {
      const c = whatsappContacts.find((x) => x.id === editingContactId);
      if (c) {
        setNewContactName(c.name);
        setNewContactNumber(c.number);
        setNewContactType(c.type);
        setNewContactIsDefault(c.isDefault);
      }
    } else {
      setNewContactName('');
      setNewContactNumber('');
      setNewContactType('Corretor');
      setNewContactIsDefault(false);
    }
  }, [editingContactId, whatsappContacts]);

  const handleSave = async () => {
    if (!user?.uid) return;
    setSaving(true);
    const ref = doc(db, 'users', user.uid, 'settings', 'chatbot');
    try {
      await setDoc(
        ref,
        {
          assistantName,
          responseStyle,
          initialGreeting,
          defaultLanguage,
          primaryColor,
          whatsapp_contacts: whatsappContacts,
          askNameProactively,
        },
        { merge: true }
      );
      toast.success(intl.formatMessage({ id: 'chatbotSettings.saved' }));
    } catch (err) {
      console.error('Error saving settings:', err);
      toast.error(
        intl.formatMessage({
          id: 'chatbotSettings.saveError',
          defaultMessage: 'Erro ao salvar configurações.',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewClick = async () => {
    if (!user?.uid) return;
    const userProps = await getUserProperties(user.uid);
    if (!userProps.length) {
      toast.warn(
        intl.formatMessage({
          id: 'chatbotSettings.needProperties',
          defaultMessage:
            'Você precisa ter ao menos um imóvel cadastrado para usar esta função.',
        })
      );
      return;
    }
    setProperties(userProps);
    setPreviewOpen(true);
  };

  const handleAddContact = () => {
    if (!newContactName.trim() || !newContactNumber.trim()) {
      alert(intl.formatMessage({ id: 'chatbotSettings.whatsapp.validationError' }));
      return;
    }
    const cleaned = newContactNumber.trim().replace(/[^+\d]/g, '');
    if (whatsappContacts.some((c) => c.number.replace(/[^+\d]/g, '') === cleaned)) {
      alert(
        intl.formatMessage({
          id: 'chatbotSettings.whatsapp.duplicateError',
          defaultMessage: 'Número de WhatsApp já cadastrado.',
        })
      );
      return;
    }
    setIsContactSaving(true);
    const newContact = {
      id: `${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      name: newContactName.trim(),
      number: newContactNumber.trim(),
      type: newContactType,
      isDefault: newContactIsDefault,
      status: 'disponivel',
    };
    let updated = [...whatsappContacts];
    if (newContactIsDefault) updated = updated.map((c) => ({ ...c, isDefault: false }));
    updated.push(newContact);
    if (updated.length > 0 && !updated.some((c) => c.isDefault)) updated[0].isDefault = true;
    setWhatsappContacts(updated);
    setNewContactName('');
    setNewContactNumber('');
    setNewContactType('Corretor');
    setNewContactIsDefault(false);
    setIsContactSaving(false);
  };

  const handleDeleteContact = (contactId) => {
    const updated = whatsappContacts.filter((c) => c.id !== contactId);
    if (updated.length > 0 && !updated.some((c) => c.isDefault)) updated[0].isDefault = true;
    setWhatsappContacts(updated);
  };

  const handleSaveEdit = () => {
    if (!newContactName.trim() || !newContactNumber.trim()) {
      alert(intl.formatMessage({ id: 'chatbotSettings.whatsapp.validationError' }));
      return;
    }
    const cleaned = newContactNumber.trim().replace(/[^+\d]/g, '');
    if (
      whatsappContacts.some(
        (c) => c.id !== editingContactId && c.number.replace(/[^+\d]/g, '') === cleaned
      )
    ) {
      alert(
        intl.formatMessage({
          id: 'chatbotSettings.whatsapp.duplicateError',
          defaultMessage: 'Número de WhatsApp já cadastrado.',
        })
      );
      return;
    }
    setIsContactSaving(true);
    let updated = whatsappContacts.map((c) =>
      c.id === editingContactId
        ? {
            ...c,
            name: newContactName.trim(),
            number: newContactNumber.trim(),
            type: newContactType,
            isDefault: newContactIsDefault,
          }
        : { ...c, isDefault: newContactIsDefault ? false : c.isDefault }
    );
    if (updated.length > 0 && !updated.some((c) => c.isDefault)) {
      const edited = updated.find((c) => c.id === editingContactId);
      if (edited) edited.isDefault = true;
      else updated[0].isDefault = true;
    }
    setWhatsappContacts(updated);
    setEditingContactId(null);
    setNewContactName('');
    setNewContactNumber('');
    setNewContactType('Corretor');
    setNewContactIsDefault(false);
    setIsContactSaving(false);
  };

  const handleToggleStatus = (contactId) => {
    const updated = whatsappContacts.map((c) =>
      c.id === contactId
        ? { ...c, status: c.status === 'disponivel' ? 'indisponivel' : 'disponivel' }
        : c
    );
    setWhatsappContacts(updated);
  };

  if (!user?.uid) {
    return (
      <div className="la loading-row">
        <CircularProgress size={20} />
        <span>{intl.formatMessage({ id: 'auth.userNotLoaded' })}</span>
      </div>
    );
  }

  return (
    <div className="la">
      <div className="dashboard-content">
        <h1 className="page-title">{intl.formatMessage({ id: 'chatbotSettings.title' })}</h1>

        <section className="card card-dashed mb-16">
          <p className="subtitle-muted mb-0">
            {intl.formatMessage({ id: 'chatbotSettings.description' })}
          </p>
        </section>

        <section className="card mb-16">
          <div className="section-title">
            {intl.formatMessage({ id: 'chatbot.assistantConfigTitle' })}
          </div>

          <div className="form-grid mt-16">
            <div className="form-group">
              <label>{intl.formatMessage({ id: 'chatbotSettings.assistantName' })}</label>
              <input
                className="input-field"
                type="text"
                value={assistantName}
                onChange={(e) => setAssistantName(e.target.value)}
                placeholder={intl.formatMessage({ id: 'chatbotSettings.namePlaceholder' })}
              />
            </div>

            <div className="form-group">
              <label>{intl.formatMessage({ id: 'chatbotSettings.defaultLanguage' })}</label>
              <select
                className="input-field"
                value={defaultLanguage}
                onChange={(e) => setDefaultLanguage(e.target.value)}
                aria-label={intl.formatMessage({ id: 'chatbotSettings.defaultLanguage' })}
              >
                <option value="pt">Português</option>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>
                {intl.formatMessage({ id: 'chatbotSettings.responseStyle' })}{' '}
                <span title={intl.formatMessage({ id: 'chatbotSettings.style.help' })}>❓</span>
              </label>
              <select
                className="input-field"
                value={responseStyle}
                onChange={(e) => setResponseStyle(e.target.value)}
                aria-label={intl.formatMessage({ id: 'chatbotSettings.responseStyle' })}
              >
                <option value="objective">
                  {intl.formatMessage({ id: 'chatbotSettings.style.objective' })}
                </option>
                <option value="conversational">
                  {intl.formatMessage({ id: 'chatbotSettings.style.conversational' })}
                </option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>{intl.formatMessage({ id: 'chatbotSettings.greeting' })}</label>
              <input
                className="input-field"
                type="text"
                value={initialGreeting}
                onChange={(e) => setInitialGreeting(e.target.value)}
                placeholder={intl.formatMessage({ id: 'chatbotSettings.greetingPlaceholder' })}
              />
            </div>

            <div className="form-group full-width">
              <label>{intl.formatMessage({ id: 'chatbotSettings.color' })}</label>
              <div className="row gap-10">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="color-swatch-20"
                  aria-label={intl.formatMessage({ id: 'chatbotSettings.color' })}
                />
                <input
                  className="input-field max-160"
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  aria-label={intl.formatMessage({ id: 'chatbotSettings.color' })}
                />
              </div>
            </div>

            {/* Checkbox – com wrapper neutro para evitar interferências externas */}
            <div className="form-group full-width">
              <div className="checkline">
                <input
                  id="askNameProactively"
                  type="checkbox"
                  checked={askNameProactively}
                  onChange={(e) => setAskNameProactively(e.target.checked)}
                />
                <label htmlFor="askNameProactively">
                <strong>
                  {intl.formatMessage({
                    id: 'chatbotSettings.askNameProactively',
                    defaultMessage: 'Perguntar nome no início da conversa?',
                  })}
                 </strong>
                </label>
              </div>
            </div>
          </div>
        </section>

        <section className="card mb-16">
          <div className="section-title">
            {intl.formatMessage({
              id: 'chatbotSettings.whatsapp.title',
              defaultMessage: 'Contato via WhatsApp',
            })}
          </div>
          <p className="subtitle-muted mt-16">
            {intl.formatMessage({
              id: 'chatbotSettings.whatsapp.description',
              defaultMessage:
                'Cadastre os números de WhatsApp da imobiliária ou corretor para encaminhamento de leads.',
            })}
          </p>

          {whatsappContacts.length === 0 ? (
            <div className="status-box mt-16">
              {intl.formatMessage({
                id: 'chatbotSettings.whatsapp.noContacts',
                defaultMessage: 'Nenhum contato de WhatsApp cadastrado ainda.',
              })}
            </div>
          ) : (
            <ul className="wac-list mt-16">
              {whatsappContacts.map((c) => (
                <li key={c.id} className="card wac-item">
                  <div className="wac-main">
                    <span className={`status-dot ${c.status === 'disponivel' ? 'on' : 'off'}`} />
                    <div className="wac-text">
                      <div className="wac-title">
                        <strong>{c.name}</strong>
                        <span className="muted"> ({c.type})</span>
                        {c.isDefault && (
                          <span className="wac-default">
                            (
                            {intl.formatMessage({
                              id: 'chatbotSettings.whatsapp.default',
                              defaultMessage: 'Padrão',
                            })}
                            )
                          </span>
                        )}
                      </div>
                      <div className="wac-sub muted break-anywhere">{c.number}</div>
                    </div>
                  </div>

                  <div className="wac-actions">
                    <button
                      type="button"
                      onClick={() => handleToggleStatus(c.id)}
                      className="secondary-button"
                    >
                      {c.status === 'disponivel'
                        ? intl.formatMessage({
                            id: 'chatbotSettings.whatsapp.makeUnavailable',
                            defaultMessage: 'Indisponibilizar',
                          })
                        : intl.formatMessage({
                            id: 'chatbotSettings.whatsapp.makeAvailable',
                            defaultMessage: 'Disponibilizar',
                          })}
                    </button>

                    {editingContactId !== c.id && (
                      <button
                        type="button"
                        onClick={() => setEditingContactId(c.id)}
                        className="secondary-button"
                      >
                        {intl.formatMessage({ id: 'general.edit' })}
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => handleDeleteContact(c.id)}
                      className="danger-button"
                    >
                      {intl.formatMessage({ id: 'general.delete' })}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card mb-16">
          <div className="section-title">
            {editingContactId
              ? intl.formatMessage({
                  id: 'chatbotSettings.whatsapp.editContactTitle',
                  defaultMessage: 'Editar Contato',
                })
              : intl.formatMessage({
                  id: 'chatbotSettings.whatsapp.addContactTitle',
                  defaultMessage: 'Adicionar Novo Contato',
                })}
          </div>

          <div className="grid-add-row mt-16">
            <input
              type="text"
              className="input-field"
              placeholder={intl.formatMessage({
                id: 'chatbotSettings.whatsapp.namePlaceholder',
                defaultMessage: 'Ex: Corretor João',
              })}
              value={newContactName}
              onChange={(e) => setNewContactName(e.target.value)}
            />
            <input
              type="text"
              className="input-field"
              placeholder={intl.formatMessage({
                id: 'chatbotSettings.whatsapp.numberPlaceholder',
                defaultMessage: '+55 11 98765-4321',
              })}
              value={newContactNumber}
              onChange={(e) => setNewContactNumber(e.target.value)}
            />
            <select
              className="input-field"
              value={newContactType}
              onChange={(e) => setNewContactType(e.target.value)}
            >
              <option value="Corretor">
                {intl.formatMessage({
                  id: 'chatbotSettings.whatsapp.type.agent',
                  defaultMessage: 'Corretor',
                })}
              </option>
              <option value="Comercial">
                {intl.formatMessage({
                  id: 'chatbotSettings.whatsapp.type.commercial',
                  defaultMessage: 'Comercial',
                })}
              </option>
              <option value="Suporte">
                {intl.formatMessage({
                  id: 'chatbotSettings.whatsapp.type.support',
                  defaultMessage: 'Suporte',
                })}
              </option>
            </select>

            <div className="row center gap-10">
              <input
                id="newContactIsDefault"
                type="checkbox"
                checked={newContactIsDefault}
                onChange={(e) => setNewContactIsDefault(e.target.checked)}
              />
              <label htmlFor="newContactIsDefault">
                {intl.formatMessage({
                  id: 'chatbotSettings.whatsapp.useAsDefault',
                  defaultMessage: 'Usar como padrão para encaminhamento',
                })}
              </label>
            </div>

            {editingContactId ? (
              <>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={isContactSaving}
                  className="primary-button"
                >
                  {isContactSaving
                    ? intl.formatMessage({ id: 'general.saving' })
                    : intl.formatMessage({ id: 'general.save' })}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingContactId(null)}
                  className="secondary-button"
                >
                  {intl.formatMessage({ id: 'general.cancel' })}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleAddContact}
                disabled={isContactSaving}
                className="primary-button"
              >
                {isContactSaving
                  ? intl.formatMessage({ id: 'general.saving' })
                  : intl.formatMessage({
                      id: 'chatbotSettings.whatsapp.addButton',
                      defaultMessage: 'Adicionar Contato',
                    })}
              </button>
            )}
          </div>
        </section>

        {/* Ações principais / Preview (alinhado à direita) */}
        <div className="row gap-10 justify-end">
          <button onClick={handleSave} className="primary-button" disabled={saving}>
            {saving
              ? intl.formatMessage({ id: 'general.saving' })
              : intl.formatMessage({ id: 'chatbotSettings.save' })}
          </button>
          <button type="button" onClick={handlePreviewClick} className="secondary-button">
            {intl.formatMessage({ id: 'chatbotSettings.preview' })}
          </button>
        </div>

        <ChatbotPreview
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
          placeholderPrompt={initialGreeting}
          settings={{
            assistantName,
            responseStyle,
            defaultLanguage,
            primaryColor,
            whatsapp_contacts: whatsappContacts,
            askNameProactively,
          }}
          user={user}
          properties={properties}
        />
      </div>
    </div>
  );
}
